import React, { useEffect } from 'react';

const DeepDive = () => {
  useEffect(() => {
    const handleScroll = () => {
      const blocks = document.querySelectorAll('.content-block');
      blocks.forEach(block => {
        const rect = block.getBoundingClientRect();
        // Fade in when enters viewport center
        if (rect.top < window.innerHeight * 0.75 && rect.bottom > 0) {
          block.classList.add('visible');
        } else {
          block.classList.remove('visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div id="deep-dive-layer">
      {/* 1. Motivation */}
      <div className="content-block">
        <span className="chapter">01 / Concept</span>
        <h3>The Cognitive Foundation</h3>
        <p>Current multi-agent AI systems excel at generating text, analyzing data, and executing workflow steps.
        But they fail at continuity—an agent rarely understands how the user normally thinks, how problems were
        solved in the past, or what behavioral patterns the user follows under pressure.</p>
        <p>Neuro Vector Memory (NVM) is Timelit's breakthrough solution: a two-layer memory architecture that
        gives digital agents a stable sense of experience, style, and behavioral alignment with the
        user—functionally acting as a second digital brain.</p>

        <h4>Two Memory Systems</h4>
        <p>Human problem-solving relies on two complementary memory systems that enable true intelligence:</p>
        <ul>
          <li><strong>Episodic Memory</strong>: Specific past events, conversations, documents, and interactions
            that provide factual context.</li>
          <li><strong>Behavioral Memory</strong>: Stable patterns of decision-making, communication style, and
            problem-solving approaches that define how individuals handle recurring challenges.</li>
        </ul>
        <p>Traditional vector stores capture only episodic memory, creating "stateless" AI that treats every
        interaction as novel. NVM introduces the missing behavioral layer.</p>
      </div>

      {/* 2. Architecture */}
      <div className="content-block">
        <span className="chapter">02 / Architecture</span>
        <h3>Layer 1 & Layer 2</h3>

        <h4>Layer 1 – The Episodic Memory Store</h4>
        <p>Layer 1 contains vector embeddings of all atomic data points in the user's digital workspace: emails,
        chat messages, documents, transcriptions, meeting summaries, and previous agent outputs.</p>
        <p>These embeddings enable classical semantic search. But they remain context-free: each item is treated as
        a standalone artefact.</p>

        <h4>Layer 2 – Behavioral Memory Store</h4>
        <p>Layer 2 introduces a revolutionary second class of embeddings that capture behavioral intelligence:</p>
        <p><strong>1. Task-Episode Memory</strong></p>
        <p>For every completed task, NVM creates a roadmap summarizing:</p>
        <ul>
          <li><strong>Trigger Signature</strong>: What started this (email topic, sender, urgency)</li>
          <li><strong>Intent</strong>: What the user understood as the goal</li>
          <li><strong>Workflow Steps</strong>: Ordered list of actions taken</li>
          <li><strong>Tone Style</strong>: Quick, reassuring, technical deep dive, etc.</li>
          <li><strong>Outcome</strong>: Success, escalation, or follow-up</li>
        </ul>

        <p><strong>2. Behavioral-Trait Memory</strong></p>
        <p>NVM learns user personality through continuous analysis: Communication Style, Decision Preferences (risk
        tolerance, speed), Stress Response, and Domain Expertise.</p>
      </div>

      {/* 3. Retrieval */}
      <div className="content-block">
        <span className="chapter">03 / Process</span>
        <h3>Contextual Semantic Search</h3>
        <p>NVM executes a sophisticated four-stage retrieval process that combines factual and behavioral
        intelligence:</p>
        <ol style={{color: '#bbb', lineHeight: '1.8', marginBottom: '1.5rem'}}>
          <li><strong>Pattern Matching</strong>: New tasks are embedded and matched against similar past episodes
            and behavioral patterns.</li>
          <li><strong>Behavioral Alignment</strong>: Relevant personality traits and decision styles are retrieved
            to guide agent behavior.</li>
          <li><strong>Context Enrichment</strong>: The system expands to include linked documents, emails, and
            communications.</li>
          <li><strong>Intelligent Re-ranking</strong>: Content is prioritized based on behavioral relevance and
            problem-solving success.</li>
        </ol>
        <p>This creates true contextual intelligence: agents receive not just relevant documents, but the behavioral
        blueprint of "how this type of problem was successfully solved before".</p>
      </div>

      {/* 4. Impact (New Section) */}
      <div className="content-block">
        <span className="chapter">04 / Experience</span>
        <h3>The Behavioral Difference</h3>
        <p>See how NVM transforms a generic AI interaction into a personalized, context-aware workflow.</p>

        <div style={{marginBottom: '20px', fontFamily: "'JetBrains Mono', monospace", color: '#94a3b8', fontSize: '0.9rem'}}>
          <span style={{color: '#06b6d4'}}>&gt;</span> User Prompt: "Draft a reply to John about the API limits."
        </div>

        <div className="comparison-container">
          {/* Standard Card */}
          <div className="comparison-card standard">
            <div className="card-header">
              <span>Standard AI</span>
              <span className="badge standard">Stateless</span>
            </div>
            <div className="card-body" dangerouslySetInnerHTML={{
              __html: '"Dear John,<br><br>Thank you for contacting us regarding the API limits. We understand your concern and apologize for any inconvenience. Please let us know if you would like to upgrade your plan."'
            }}>
            </div>
            <div className="card-meta">
              <div className="meta-item"><div className="meta-icon off"></div> Memory: None</div>
              <div className="meta-item">Style: Generic / Polite</div>
            </div>
          </div>

          {/* NVM Card */}
          <div className="comparison-card nvm">
            <div className="card-header">
              <span style={{color: '#fff'}}>Timelit NVM</span>
              <span className="badge nvm">Context-Aware</span>
            </div>
            <div className="card-body" dangerouslySetInnerHTML={{
              __html: '"John - saw your note. We\'re hitting the standard rate limit (100/sec) because of the new batch job.<br><br>I can bump you to the enterprise tier for 48h while we optimize. Let me know."'
            }}>
            </div>
            <div className="card-meta">
              <div className="meta-item"><div className="meta-icon on"></div> Context: 'Batch Job' Email</div>
              <div className="meta-item"><div className="meta-icon on"></div> Style: Direct / Solution-Focused</div>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Technical */}
      <div className="content-block">
        <span className="chapter">05 / Technology</span>
        <h3>Technical Implementation</h3>
        <p>NVM leverages proven enterprise technologies for robust, scalable deployment.</p>

        <h4>Behavioral Scoring System</h4>
        <p>NVM implements a multi-dimensional scoring framework that evaluates behavioral patterns across five key
        dimensions:</p>
        <div className="code-block">
          Composite Score =<br/>
          (Outcome Quality * 0.40) +<br/>
          (Process Efficiency * 0.25) +<br/>
          (Behavioral Consistency * 0.20) +<br/>
          (User Feedback * 0.10) +<br/>
          (Learning Impact * 0.05)
        </div>
        <p><strong>Learning Algorithm</strong>: Scores update using exponential moving average to balance stability
        with adaptation (10% weight to new outcomes, 90% to historical performance).</p>

        <h4>Multi-Dimensional Embedding Pipeline</h4>
        <p>For each task-episode, we generate a composite embedding:</p>
        <div className="code-block">
          composite_embedding = weighted_sum([<br/>
          &nbsp;&nbsp;embed(trigger + intent),<br/>
          &nbsp;&nbsp;embed(context),<br/>
          &nbsp;&nbsp;embed(workflow + tools),<br/>
          &nbsp;&nbsp;embed(outcome + tone)<br/>
          ])
        </div>
      </div>

      {/* 6. Privacy */}
      <div className="content-block">
        <span className="chapter">06 / Safety</span>
        <h3>Privacy Protection System</h3>

        <h4>Inferential Privacy Filtering</h4>
        <p>Advanced filtering removes patterns that could reveal protected characteristics. The system actively
        sanitizes:</p>
        <ul>
          <li><strong>Health Patterns</strong>: Stress responses indicating medical conditions.</li>
          <li><strong>Financial Patterns</strong>: Indicators of income levels or financial stress.</li>
          <li><strong>Personal Relationships</strong>: Patterns exposing family dynamics.</li>
        </ul>

        <h4>Compliance</h4>
        <p><strong>EU AI Act Compliance</strong>: Automated risk assessments for high-impact behavioral
        recommendations.</p>
        <p><strong>GDPR Alignment</strong>: User data minimization and right to erasure for behavioral models.</p>
      </div>

      {/* 6. Vision */}
      <div className="content-block end-marker">
        <h3>The Vision</h3>
        <p>Timelit's NVM is not just another AI feature—it's the foundation for the future of human-AI
        collaboration, where artificial intelligence becomes genuinely intelligent about how humans work.</p>
        <p><strong>Truly Adaptive. Synergistic. Institutional Memory.</strong></p>
      </div>
    </div>
  );
};

export default DeepDive;
